/**
 * GFG-194 (Send Queue, GFG Admin) — unit U14. The screen itself: the failure banner (list
 * request, filter-options request, or both), and the Blotter, plus every dialog the row menu can
 * open and the toast that reports the outcome of a mutation or an export.
 *
 * This is the only unit that calls the mutation hooks. The row menu fires `onAction(kind, item)`
 * and the three confirm/form modals fire `onConfirm` / `onSave(iso)`; none of them touches the
 * API. There is NO optimistic update anywhere: `useSendQueueActions` invalidates the whole
 * `["admin", "send-queue"]` prefix on success, so the table refetches — which is what the
 * acceptance criterion ("after any action the table refreshes") asks for literally. Every
 * mutation is funnelled through `runRowAction`, which owns the double-submit guard; nothing here
 * calls `mutate` directly.
 *
 * Shape follows `components/admin/contacts/AdminContactsPanel.tsx`, the same screen one ticket
 * older: a `useData` factory (see `makeUseSendQueueData`), a failure Alert rendered ABOVE the
 * Blotter rather than in place of it, and Retry as a prefix invalidation.
 */
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Snackbar,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchSendQueue } from "@/api/sendQueueCampaigns";
import ConfirmActionModal from "@/components/admin/sendQueueCampaigns/ConfirmActionModal";
import RescheduleModal from "@/components/admin/sendQueueCampaigns/RescheduleModal";
import {
  type SendQueueRowActionKind,
  SendQueueRowMenu,
} from "@/components/admin/sendQueueCampaigns/SendQueueRowMenu";
import { buildColumns } from "@/components/admin/sendQueueCampaigns/sendQueueCampaignsColumns";
import ViewEmailDialog from "@/components/admin/sendQueueCampaigns/ViewEmailDialog";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter, FilterOption } from "@/components/BlotterFilterBar";
import EmailPreview from "@/components/campaignBuilder/design/EmailPreview";
import CampaignContactsModal from "@/components/contacts/CampaignContactsModal";
import type { CampaignContactRow } from "@/components/contacts/types";
import { GfgSelect } from "@/components/ui/GfgSelect";
import { CAMPAIGN_STATUSES, statusVisual } from "@/constants/campaings";
import {
  CAMPAIGN_TYPE_OPTIONS,
  COPY,
  CSV_COLUMNS,
  CSV_FILENAME,
  DEFAULT_WINDOW,
  MIN_WIDTH,
  PAGE_SIZE,
  SEND_TYPE_FILTER_OPTIONS,
  WINDOW_OPTIONS,
} from "@/constants/sendQueueCampaigns";
import {
  useSendQueue,
  useSendQueueActions,
  useSendQueueContactEmail,
  useSendQueueContacts,
  useSendQueueFilterOptions,
} from "@/hooks/useSendQueueCampaigns";
import {
  SEND_QUEUE_STATUSES,
  SEND_QUEUE_WINDOWS,
  type SendQueueContactEmailPreview,
  type SendQueueItem,
  type SendQueueStatus,
  type SendQueueWindow,
} from "@/interfaces/sendQueueCampaigns";
import { apiErrorMessage } from "@/utils/apiError-helper";
import { formatAbsolute, reminderLabel, toListParams } from "@/utils/sendQueueCampaigns-helper";

/** The query-key prefix every send-queue request shares — what Retry invalidates. */
const SEND_QUEUE_QUERY_KEY = ["admin", "send-queue"];

/** Width of the Time Window select. GfgSelect is `w-full`, so the wrapper is what sizes it. */
const WINDOW_SELECT_WIDTH = 180;

/**
 * The Status filter's options, built from the frontend's own vocabulary (`SEND_QUEUE_STATUSES`,
 * labelled through the shared status palette) rather than fetched — same reasoning as
 * AdminContactsPanel's STATUS_OPTIONS. Only Account Lead is genuinely reference data and has to
 * come from the API.
 */
const STATUS_OPTIONS: FilterOption[] = SEND_QUEUE_STATUSES.map((status) => ({
  value: status,
  label: statusVisual(status).label,
}));

/**
 * Per-row classes appended to `blotter-row` (D19). Both are set alongside `rowHover={false}`:
 * the Sending row therefore has to hand its colour through `--blotter-row-bg` as well, or the
 * no-hover rule in index.css would paint `transparent` over it under the cursor.
 */
const ROW_CLASS: Partial<Record<SendQueueStatus, string>> = {
  Sending: "bg-good-night-bg [--blotter-row-bg:var(--color-good-night-bg)]",
  Paused: "opacity-70",
};

/** Which dialog is showing, if any. One at a time — every one of them is opened from a row menu. */
type SendQueueDialog = "sendNow" | "reschedule" | "cancel" | "email" | "contacts";

interface ToastState {
  message: string;
  /** "info" is the refusal of a duplicate confirm — nothing failed, so it isn't an error. */
  severity: "success" | "error" | "info";
}

/**
 * The set of busy row ids is React state, so both of these return a NEW Set: mutating the one in
 * state would leave `useState`'s identity check unable to see the change (no re-render) while the
 * value silently drifted under every closure already holding it.
 */
function withRow(ids: ReadonlySet<number>, id: number): ReadonlySet<number> {
  const next = new Set(ids);
  next.add(id);
  return next;
}

function withoutRow(ids: ReadonlySet<number>, id: number): ReadonlySet<number> {
  const next = new Set(ids);
  next.delete(id);
  return next;
}

/** One row mutation, described so `runRowAction` can guard it without knowing which one it is. */
interface RowAction {
  /** The row being acted on — the key the guard is held under. */
  id: number;
  /** Toast wording once the API says yes. */
  successMessage: string;
  /** Toast wording when the guard refuses this action because the row is already busy. */
  busyMessage: string;
  /**
   * Fires the right mutation and hands back ITS promise — `mutateAsync`, never `mutate` with
   * per-call callbacks: one promise per invocation, owned by the caller (see `runRowAction`).
   */
  run: () => Promise<unknown>;
}

/** The banner's wording for the failure that is actually happening, or `null` when nothing is. */
function failureAlert(
  listFailed: boolean,
  optionsFailed: boolean,
): { title: string; body: string } | null {
  if (listFailed && optionsFailed) {
    return { title: COPY.failure.bothTitle, body: COPY.failure.bothBody };
  }
  if (listFailed) return { title: COPY.failure.title, body: COPY.failure.body };
  if (optionsFailed) return { title: COPY.failure.optionsTitle, body: COPY.failure.optionsBody };
  return null;
}

/**
 * The router-state message the Campaign Manager reads. Unexported and single-file by design:
 * this panel is the only sender.
 *
 * It carries the tab name and nothing else, because that is all the receiver consumes:
 * `pages/gfgAdmin/CampaignManager.tsx` runs `readTab(location.state)`, resolves the name against
 * its own TABS list and then REPLACES the entry's state with `null` — the message is consumed,
 * so it can neither replay on a remount nor go stale. No de-duplication token is needed either:
 * that effect is keyed on `location`, whose identity is one per history entry, so a second
 * navigation to the same tab is a second run on its own. There is no campaign-level target to
 * send yet — the Campaigns tab is still "Coming Soon" and reads no id.
 */
interface CampaignDeepLink {
  tab: "campaigns";
}

/** The Time Window select hands back a raw string; only the four known windows are accepted. */
function toWindow(value: string): SendQueueWindow | null {
  return SEND_QUEUE_WINDOWS.find((w) => w === value) ?? null;
}

/**
 * Builds the Send Queue blotter's data hook — a factory, for the reason AdminContactsPanel's
 * `makeUseContactsData` documents at length: `BlotterData` has no error field, so a 500 / 403 /
 * dead network would otherwise arrive as `{ data: [], total: 0, isLoading: false }` and render as
 * the ordinary empty state, with nothing upstream to catch it. This hook runs inside the
 * Blotter's render, closes over a setter this panel owns, and reports `isError` upward so the
 * panel can put a banner above `<Blotter>`.
 *
 * It also closes over the current Time Window — the Blotter's own query state has no room for it,
 * and re-creating the factory each render is what keeps the closed-over window current.
 *
 * `onError` must have a stable identity — a `useState` setter does. It is called from an effect,
 * never during render, and writing a boolean that already holds that value makes React bail out
 * of the re-render, so the report cannot loop.
 */
function makeUseSendQueueData(
  timeWindow: SendQueueWindow,
  onError: (failed: boolean) => void,
): BlotterConfig<SendQueueItem>["useData"] {
  return function useSendQueueData(q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<SendQueueItem> {
    const { data, isLoading, isError } = useSendQueue(toListParams(q, timeWindow));

    // `onError` and `timeWindow` are deliberately not dependencies: they are the factory's
    // parameters, not reactive values, and React runs the newest closure whenever `isError`
    // changes — so neither is ever stale. (Biome's useExhaustiveDependencies rejects
    // outer-scope bindings here.)
    useEffect(() => {
      onError(isError);
    }, [isError]);

    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };
}

interface ContactEmailPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  data: SendQueueContactEmailPreview | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * The contacts modal's eye icon: the personalised preview for one recipient of the currently open
 * send. Same shape as `ViewEmailDialog` (skeleton while loading, an Alert on failure, `EmailPreview`
 * on success) but for `useSendQueueContactEmail` rather than `useSendQueueEmail` -- kept local to
 * this panel rather than duplicated into a new file, since this is the only caller.
 */
function ContactEmailPreviewDialog({
  open,
  onClose,
  data,
  isLoading,
  isError,
  error,
}: ContactEmailPreviewDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {COPY.emailDialog.title}
          <IconButton onClick={onClose} aria-label={COPY.closeLabel} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        className="bg-wax flex justify-center items-start"
        sx={{ minHeight: 300, py: 4 }}
      >
        {isLoading && (
          <div className="w-full max-w-[600px] flex flex-col gap-4" aria-busy="true">
            <Skeleton variant="rounded" height={90} />
            <Skeleton variant="rounded" height={130} />
            <Skeleton variant="rounded" height={260} />
          </div>
        )}
        {!isLoading && isError && (
          <Alert severity="error" sx={{ width: "100%", maxWidth: 600 }}>
            {apiErrorMessage(error)}
          </Alert>
        )}
        {!isLoading && !isError && data && <EmailPreview state={data} isMobile={false} />}
      </DialogContent>
    </Dialog>
  );
}

export default function SendQueueCampaignsPanel() {
  const [timeWindow, setTimeWindow] = useState<SendQueueWindow>(DEFAULT_WINDOW);
  const [listFailed, setListFailed] = useState(false);
  // The toast's visibility and its content are separate state so closing it doesn't blank the
  // wording mid-fade; the last message simply holds until the next action replaces it.
  const [toast, setToast] = useState<ToastState | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  // Which dialog is open, and the row it is about. Every dialog below is mounted only while it is
  // the open one, so both are cleared together on close: there is no close transition left for a
  // held item to survive, and no dialog left holding a live Confirm button (see `closeDialog`).
  const [dialog, setDialog] = useState<SendQueueDialog | null>(null);
  const [activeItem, setActiveItem] = useState<SendQueueItem | null>(null);
  /**
   * The ids of the rows whose action has not finished landing. This panel owns the set instead of
   * reading it off the mutations, because a React Query mutation observer keeps only the MOST
   * RECENT call's `variables`: with one `useMutation` per action shared by every row, acting on
   * row B would overwrite row A's `variables` and quietly re-open row A's guard while A was still
   * in flight. A set is per-row by construction and unaffected by what any other row does.
   */
  const [busyRows, setBusyRows] = useState<ReadonlySet<number>>(() => new Set());
  // The recipient whose personalised email preview is open, or null. Independent of `dialog` --
  // this sits on TOP of the contacts modal (dialog === "contacts"), which stays open behind it.
  const [previewContact, setPreviewContact] = useState<CampaignContactRow | null>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: options, isError: optionsFailed } = useSendQueueFilterOptions();
  const actions = useSendQueueActions();
  // Only fetches while the contacts dialog is the open one; `activeItem` is set in the same call
  // that sets `dialog` (see `openDialog`), so the two are never out of step.
  const contactsQuery = useSendQueueContacts(activeItem?.id ?? 0, dialog === "contacts");
  // Gated on `previewContact` being set, same shape as `useSendQueueEmail(item?.id ?? 0, open)`.
  const contactEmailQuery = useSendQueueContactEmail(
    activeItem?.id ?? 0,
    previewContact ? Number(previewContact.id) : 0,
    previewContact != null,
  );

  const showToast = useCallback((message: string, severity: ToastState["severity"]) => {
    setToast({ message, severity });
    setToastOpen(true);
  }, []);

  /**
   * The only path from this panel to a mutation, and the whole double-submit guard.
   *
   * The row id is added to `busyRows` BEFORE the mutation is fired and removed only once the
   * invalidation-driven refetch has settled — not when the mutation resolves. That ordering is the
   * point: none of these actions is optimistic, so between the API answering and the new page
   * arriving the table still shows the row exactly as it was before the action, with the same menu
   * items enabled. Releasing on the mutation's own promise alone would re-open the guard inside
   * that window and let the user act a second time on a row the server has already moved on from.
   *
   * The guard is released from a per-CALL promise (`mutateAsync`), never from a callback handed to
   * `mutate`: a mutation observer holds a SINGLE slot for those per-call callbacks, so pausing row
   * B while row A is still in flight overwrites A's callbacks, A is never removed from `busyRows`
   * and stays un-actionable for the rest of the session. Nothing that lives on the shared observer
   * — `variables`, `isPending`, the callback slot — can identify "this call". `mutateAsync`
   * returns one promise per invocation, owned by this closure, so the `finally` below runs exactly
   * once per call whatever any other row does.
   *
   * That is also why the error toast is raised in `catch` here rather than passed as an `onError`:
   * `useSendQueueActions` declares no mutation-level `onError`, so this is the single toast for a
   * failure. And `finally` is what guarantees the FAILURE path releases too — a row left in
   * `busyRows` after a failed request is the same bug pointing the other way, and much harder to
   * notice.
   *
   * The invalidation it awaits is not a second round trip: `useSendQueueActions` has already
   * invalidated the same prefix in its own `onSuccess` (which runs before `mutateAsync` resolves),
   * and `cancelRefetch: false` makes this call ATTACH to the fetch already running. The default,
   * `true`, would CANCEL that in-flight refetch and start a competing one — two round trips per
   * action. On failure the hook invalidates nothing and this call is what refreshes the row.
   * Either way it cannot reject: `refetchQueries` swallows refetch errors unless `throwOnError`
   * is set, and `finally` would release even if it did not.
   */
  const runRowAction = useCallback(
    async ({ id, successMessage, busyMessage, run }: RowAction) => {
      if (busyRows.has(id)) {
        showToast(busyMessage, "info");
        return;
      }
      setBusyRows((current) => withRow(current, id));
      try {
        await run();
        showToast(successMessage, "success");
      } catch (error: unknown) {
        showToast(apiErrorMessage(error), "error");
      } finally {
        await queryClient.invalidateQueries(
          { queryKey: SEND_QUEUE_QUERY_KEY },
          { cancelRefetch: false },
        );
        setBusyRows((current) => withoutRow(current, id));
      }
    },
    [busyRows, queryClient, showToast],
  );

  /** Closes whichever dialog is open AND drops the row it was about — see the state's comment. */
  const closeDialog = useCallback(() => {
    setDialog(null);
    setActiveItem(null);
  }, []);

  const openDialog = useCallback((kind: SendQueueDialog, item: SendQueueItem) => {
    setActiveItem(item);
    setDialog(kind);
  }, []);

  /**
   * Both entry points to a campaign — the Campaign cell and the row menu's "View campaign" — land
   * here. `/campaigns` is the route the Campaign Manager already renders, so this switches its
   * tab rather than mounting anything new (see `CampaignDeepLink`).
   */
  const handleOpenCampaign = useCallback(() => {
    const deepLink: CampaignDeepLink = { tab: "campaigns" };
    navigate("/campaigns", { state: deepLink });
  }, [navigate]);

  /**
   * The row menu never calls the API. Pause and Resume are immediate; Send now, Reschedule and
   * Cancel go through their modal first and mutate only from its confirm handler; the three View
   * items open a read-only dialog or navigate.
   */
  const handleAction = useCallback(
    (kind: SendQueueRowActionKind, item: SendQueueItem) => {
      // The three modal actions are gated here as well as at their confirm handler: opening a
      // confirm for a row whose previous action is still landing would offer a decision about a
      // row state that no longer exists. The read-only items are never gated.
      const openGuarded = (next: SendQueueDialog) => {
        if (busyRows.has(item.id)) {
          showToast(COPY.toast.rowBusy, "info");
          return;
        }
        openDialog(next, item);
      };

      switch (kind) {
        case "pause":
          void runRowAction({
            id: item.id,
            successMessage: COPY.toast.paused(item.campaignTitle),
            busyMessage: COPY.toast.rowBusy,
            run: () => actions.pause.mutateAsync(item.id),
          });
          return;
        case "resume":
          void runRowAction({
            id: item.id,
            successMessage: COPY.toast.resumed(item.campaignTitle),
            busyMessage: COPY.toast.rowBusy,
            run: () => actions.resume.mutateAsync(item.id),
          });
          return;
        case "sendNow":
          openGuarded("sendNow");
          return;
        case "reschedule":
          openGuarded("reschedule");
          return;
        case "cancelSend":
          openGuarded("cancel");
          return;
        case "viewEmail":
          openDialog("email", item);
          return;
        case "viewContacts":
          openDialog("contacts", item);
          return;
        case "viewCampaign":
          handleOpenCampaign();
          return;
      }
    },
    [actions, busyRows, handleOpenCampaign, openDialog, runRowAction, showToast],
  );

  /**
   * The last line of the double-submit guard (before it: the modal is unmounted the moment Confirm
   * is pressed — GfgModal's primary button runs `onClose(); onPrimary();`, and MUI would otherwise
   * keep the paper mounted and clickable through its ~225ms exit transition; and the row menu
   * refuses to open this modal at all while the row is busy). Any confirm that still reaches
   * `runRowAction` for a row already in the set is refused and said so — `actionInFlight` rather
   * than `rowBusy`, because this is the wording for a Confirm on an irreversible action.
   */
  function handleConfirmSendNow() {
    if (!activeItem) return;
    const item = activeItem;
    closeDialog();
    void runRowAction({
      id: item.id,
      successMessage: COPY.toast.sendingNow(item.recipients),
      busyMessage: COPY.toast.actionInFlight,
      run: () => actions.sendNow.mutateAsync(item.id),
    });
  }

  /** Same guard as `handleConfirmSendNow`; a cancelled send cannot be un-cancelled. */
  function handleConfirmCancel() {
    if (!activeItem) return;
    const item = activeItem;
    closeDialog();
    void runRowAction({
      id: item.id,
      successMessage: COPY.toast.cancelled(reminderLabel(item)),
      busyMessage: COPY.toast.actionInFlight,
      run: () => actions.cancel.mutateAsync(item.id),
    });
  }

  function handleSaveReschedule(iso: string) {
    if (!activeItem) return;
    const item = activeItem;
    closeDialog();
    void runRowAction({
      id: item.id,
      successMessage: COPY.toast.rescheduled(formatAbsolute(iso)),
      busyMessage: COPY.toast.rowBusy,
      run: () => actions.reschedule.mutateAsync({ id: item.id, body: { scheduledAt: iso } }),
    });
  }

  /** Opens the personalised preview for one recipient of the contacts modal's currently open send. */
  const handlePreviewContactEmail = useCallback((contact: CampaignContactRow) => {
    setPreviewContact(contact);
  }, []);

  const closeContactEmailPreview = useCallback(() => {
    setPreviewContact(null);
  }, []);

  /**
   * All four per-contact endpoints (this one, remove, and the email-correction one below) are
   * REAL routes backed by the 501 stub doctrine (GFG-6) -- every one of them rejects today. That
   * is surfaced here exactly like every other row action: `apiErrorMessage(error)` reads the
   * server's own sentence and shows it in the panel's toast. There is no optimistic update and no
   * success path to soften a failure into: `useSendQueueActions`' `onSuccess` would invalidate the
   * queue on a real success, but none of these calls can reach it yet.
   *
   * `onResend` may return a promise the modal awaits before its own confirmation toast, and
   * "the caller reports its own failure" (see the modal's prop doc) -- so the rejection is
   * swallowed HERE, after this panel's own toast has already shown it, rather than re-thrown for
   * the modal to also handle.
   */
  const handleResendContact = useCallback(
    async (contact: CampaignContactRow, email: string) => {
      if (!activeItem) return;
      try {
        // The prototype lets the admin correct a bounced address and resend in one gesture, so a
        // changed address has to be persisted BEFORE the resend — otherwise the gift email would
        // go to the old one. Both calls 501 today; the ordering is what GFG-6 inherits.
        if (email !== contact.email) {
          await actions.updateContactEmail.mutateAsync({
            id: activeItem.id,
            contactId: Number(contact.id),
            body: { email },
          });
        }
        await actions.resendContact.mutateAsync({
          id: activeItem.id,
          contactId: Number(contact.id),
        });
      } catch (error: unknown) {
        showToast(apiErrorMessage(error), "error");
        // RETHROW. The modal awaits this and shows its own success toast when the promise
        // resolves; its catch is deliberately silent because this side is the only one that knows
        // what failed. Returning normally here turned every refused resend into a green
        // "Gift email resent to ..." — a success message for something that never happened.
        throw error;
      }
    },
    [activeItem, actions, showToast],
  );

  const handleRemoveContact = useCallback(
    async (contact: CampaignContactRow) => {
      if (!activeItem) return;
      try {
        await actions.removeContact.mutateAsync({
          id: activeItem.id,
          contactId: Number(contact.id),
        });
      } catch (error: unknown) {
        // NO rethrow here, unlike handleResendContact. The modal fires `onRemove?.(contact)`
        // without awaiting it and shows no success toast, so there is no false success to
        // prevent — and a rejection nobody awaits surfaces as an unhandled promise rejection.
        // Rethrow only where the caller awaits the promise AND has a success path.
        showToast(apiErrorMessage(error), "error");
      }
    },
    [activeItem, actions, showToast],
  );

  const handleContactEmailChange = useCallback(
    async (contact: CampaignContactRow, email: string) => {
      if (!activeItem) return;
      try {
        await actions.updateContactEmail.mutateAsync({
          id: activeItem.id,
          contactId: Number(contact.id),
          body: { email },
        });
      } catch (error: unknown) {
        // No rethrow, same reason as handleRemoveContact: the modal commits the edit on blur with
        // `onEmailChange?.(contact, next)` and never awaits it.
        showToast(apiErrorMessage(error), "error");
      }
    },
    [activeItem, actions, showToast],
  );

  /**
   * Retry invalidates the whole `["admin", "send-queue"]` branch, which covers the list —
   * whatever search / filters / sort / page the Blotter currently holds, which this panel
   * deliberately cannot see — along with the filter options. `isError` flips back to false on
   * success, so the banner clears itself.
   */
  const handleRetry = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: SEND_QUEUE_QUERY_KEY });
  }, [queryClient]);

  const config: BlotterConfig<SendQueueItem> = {
    // No title: the tab bar directly above already reads "Send Queue".
    searchPlaceholder: COPY.searchPlaceholder,
    itemNoun: COPY.itemNoun,
    emptyMessage: COPY.emptyState,
    minWidth: MIN_WIDTH,
    pageSize: PAGE_SIZE,
    defaultSortKey: "scheduled_at",
    rowKey: (r) => r.id,
    useData: makeUseSendQueueData(timeWindow, setListFailed),
    columns: buildColumns({
      onOpenCampaign: handleOpenCampaign,
      renderActions: (item) => <SendQueueRowMenu item={item} onAction={handleAction} />,
    }),
    // Ids must match what `toListParams` reads — the API's DTO silently discards values it does
    // not recognise, so a typo here would make the filter do nothing rather than error.
    filterFields: [
      {
        id: "campaign_type",
        label: COPY.filterLabels.campaignType,
        options: CAMPAIGN_TYPE_OPTIONS,
      },
      { id: "send_type", label: COPY.filterLabels.sendType, options: SEND_TYPE_FILTER_OPTIONS },
      { id: "status", label: COPY.filterLabels.status, options: STATUS_OPTIONS },
      {
        id: "account_lead",
        label: COPY.filterLabels.accountLead,
        // Empty whenever the options request failed — which the banner above says out loud.
        options: options?.accountLeads ?? [],
      },
    ],
    exportFilename: CSV_FILENAME,
    csvColumns: CSV_COLUMNS,
    // Same params as the table (including the Time Window), so the CSV covers every filtered row
    // across every page — the Blotter walks the pages itself. The export query carries no sort:
    // `toListParams` drops the unrecognised key and the API applies its own default order.
    // A failed page REJECTS. The catch is only here to say so in the panel's own error toast —
    // the Blotter has no error surface of its own — and it rethrows, because this promise is the
    // only signal the Blotter's page walk has. Swallowing the rejection and returning
    // `{ data: [], totalPages: 0 }` also stops the walk, but stops it *successfully*: the Blotter
    // would then hand the pages that happened to load to `downloadCsv`, and the user would be
    // given a truncated (or header-only) file believing it to be the whole filtered set. A
    // download that never starts is recoverable; one that is silently short is not. The Blotter
    // side owns aborting the download on a rejected page.
    exportFetch: async (q) => {
      try {
        const res = await fetchSendQueue(
          toListParams({ ...q, sortBy: "", sortDir: "asc" }, timeWindow),
        );
        return { data: res.data, totalPages: res.totalPages };
      } catch (error: unknown) {
        showToast(apiErrorMessage(error, COPY.toast.exportFailed), "error");
        throw error;
      }
    },
    // "start" puts the Time Window between the filter bar and the Export CSV button.
    headerActionPlacement: "start",
    headerAction: (
      <div style={{ width: WINDOW_SELECT_WIDTH }}>
        <GfgSelect
          label={COPY.windowLabel}
          options={WINDOW_OPTIONS}
          value={timeWindow}
          onChange={(e) => {
            const next = toWindow(e.target.value);
            if (next) setTimeWindow(next);
          }}
        />
      </div>
    ),
    // A narrower window can leave fewer pages than the one the user is on — without this, a user
    // on page 7 of a 30-day view who switches to 24 hours lands on an empty page.
    pageResetKey: timeWindow,
    rowHover: false,
    rowClassName: (r) => ROW_CLASS[r.status],
  };

  const failure = failureAlert(listFailed, optionsFailed);

  return (
    <>
      {/* Above the Blotter, never in place of it: replacing the table would throw away the
          search, filters, sort and page the user is retrying with. Covers the options request
          too — its failure empties the Account Lead menu, which is otherwise indistinguishable
          from "this account has no leads". */}
      {failure && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mt: 3, mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              {COPY.failure.retry}
            </Button>
          }
        >
          <AlertTitle>{failure.title}</AlertTitle>
          {failure.body}
        </Alert>
      )}
      <div style={{ marginTop: failure ? 0 : 24 }}>
        <Blotter config={config} />
      </div>

      {/* Every dialog is mounted only while it is the open one. For the two irreversible
          confirms that is the point: pressing Confirm removes the button from the DOM in the same
          commit, so the double-click that MUI's exit transition would otherwise allow has nothing
          left to hit. The read-only dialogs follow the same shape for consistency, and it retires
          their "item may be null while open is true" transient. */}
      {dialog === "sendNow" && activeItem && (
        <ConfirmActionModal
          context="sendNow"
          item={activeItem}
          onClose={closeDialog}
          onConfirm={handleConfirmSendNow}
        />
      )}
      {dialog === "cancel" && activeItem && (
        <ConfirmActionModal
          context="cancel"
          item={activeItem}
          onClose={closeDialog}
          onConfirm={handleConfirmCancel}
        />
      )}
      {dialog === "reschedule" && activeItem && (
        <RescheduleModal item={activeItem} onClose={closeDialog} onSave={handleSaveReschedule} />
      )}
      {dialog === "email" && activeItem && (
        <ViewEmailDialog item={activeItem} open onClose={closeDialog} />
      )}
      {dialog === "contacts" && activeItem && (
        <CampaignContactsModal
          open
          onClose={closeDialog}
          campaignName={activeItem.campaignTitle}
          contacts={contactsQuery.data?.data ?? []}
          total={contactsQuery.data?.total}
          loading={contactsQuery.isLoading}
          error={contactsQuery.isError ? apiErrorMessage(contactsQuery.error) : null}
          // A queued row can be Paused (SEND_QUEUE_STATUSES includes it, and the row menu offers
          // Pause/Resume), and a paused campaign is exactly the prototype's "paused" lock: its
          // contacts cannot be managed until it is resumed. "closed" is genuinely unreachable
          // here -- the queue only lists sends that are still going to happen.
          lock={activeItem.status === CAMPAIGN_STATUSES.QUEUE_PAUSED ? "paused" : null}
          onResend={handleResendContact}
          onPreviewEmail={handlePreviewContactEmail}
          onRemove={handleRemoveContact}
          onEmailChange={handleContactEmailChange}
          // MVP: the icons show, greyed and inert. Every write endpoint behind them answers 501
          // until GFG-6 lands (see sendQueueService.ts), so an admin clicking one would get a
          // failure and no explanation. The handlers above stay wired — delete this one line to
          // turn the column on the day the endpoints exist.
          actionsDisabled
        />
      )}

      <ContactEmailPreviewDialog
        open={previewContact != null}
        onClose={closeContactEmailPreview}
        data={contactEmailQuery.data}
        isLoading={contactEmailQuery.isLoading}
        isError={contactEmailQuery.isError}
        error={contactEmailQuery.error}
      />

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast?.severity ?? "success"}
          variant="filled"
          onClose={() => setToastOpen(false)}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
